import { DatePicker } from '@/components/date-picker';
import LotCard from '@/components/lot/LotCard';
import { Button } from '@/components/shadcn-ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn-ui/card';
import { Dialog, DialogClose, DialogContent, DialogHeader } from '@/components/shadcn-ui/dialog';
import { Input } from '@/components/shadcn-ui/input';
import { Label } from '@/components/shadcn-ui/label';
import { cn } from '@/lib/utils';
import { getLotById, preProcessUpdate } from '@/network/api.service';
import preProcessingSchema, { preProcessingPartialSchema } from '@/schema/pre-process-form-schema';
import { useLotStore } from '@/stores/useLot.store';
import { useUserStore } from '@/stores/useUser.store';
import { yupResolver } from '@hookform/resolvers/yup';
import { DialogTitle } from '@radix-ui/react-dialog';
import { CircleAlert, Loader2, TriangleAlert } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const PreProcessEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const lot = useLotStore(state => state.lot);
  const user = useUserStore(state => state.user);

  const [isSaving, setIsSaving] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [showMandatoryDialog, setShowMandatoryDialog] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    clearErrors,
    setError,
  } = useForm({
    resolver: yupResolver(preProcessingPartialSchema),
    defaultValues: {
      input_coffee_received_kg: lot?.pre_process_data?.input_coffee_received_kg || lot?.weight_of_coffee || '',
      winnower_waste_kg: lot?.pre_process_data?.winnower_waste_kg || '',
      siphon_rejects_kg: lot?.pre_process_data?.siphon_rejects_kg || '',
      unripe_coffees_kg: lot?.pre_process_data?.unripe_coffees_kg || '',
      ripe_coffee_for_processing_kg: lot?.pre_process_data?.ripe_coffee_for_processing_kg || '',
      completion_of_pre_processing_at: lot?.pre_process_data?.completion_of_pre_processing_at
        ? new Date(lot.pre_process_data.completion_of_pre_processing_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : new Date(),
    },
  });

  useEffect(() => {
    if (!lot) return;
    reset({
      input_coffee_received_kg: lot?.pre_process_data?.input_coffee_received_kg || lot?.weight_of_coffee || '',
      winnower_waste_kg: lot?.pre_process_data?.winnower_waste_kg || '',
      siphon_rejects_kg: lot?.pre_process_data?.siphon_rejects_kg || '',
      unripe_coffees_kg: lot?.pre_process_data?.unripe_coffees_kg || '',
      ripe_coffee_for_processing_kg: lot?.pre_process_data?.ripe_coffee_for_processing_kg || '',
      completion_of_pre_processing_at: lot?.pre_process_data?.completion_of_pre_processing_at
        ? new Date(lot.pre_process_data.completion_of_pre_processing_at)
        : new Date(),
    });
  }, [lot, reset]);

  const fetchLot = async () => {
    try {
      const { data } = await getLotById(id, false);
      if (data?.data) {
        useLotStore.getState().setLot({ ...data.data });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchLot();
  }, [id]);

  const onSaveDetails = async data => {
    setIsSaving(true);
    try {
      const cleanedData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => {
          return value !== '' && value !== null && value !== undefined;
        })
      );

      const { data: respData } = await preProcessUpdate(
        {
          pre_process_data: { ...cleanedData, updated_by: user?.user_id },
          action: 'save',
        },
        id
      );
      if (respData.success) {
        toast.success('Lot Information Saved');
      }
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const onMoveToProcessing = async data => {
    setIsMoving(true);
    try {
      await preProcessingSchema.validate(data, { abortEarly: false });
      clearErrors();

      const { data: respData } = await preProcessUpdate(
        {
          pre_process_data: { ...data, updated_by: user?.user_id },
          action: 'move_to_processing',
          stage: 'Process',
        },
        id
      );

      if (respData.success) {
        navigate('/');
      }
    } catch (error) {
      // Map field errors
      if (error.inner && Array.isArray(error.inner)) {
        error.inner.forEach(e => {
          if (!e.path) return;
          setError(e.path, {
            type: 'manual',
            message: e.message,
          });
        });
      }

      setShowMandatoryDialog(true);
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col space-y-[18px] px-4 py-[18px]">
      <div className="">
        <LotCard lot={lot} edit={true} />
      </div>

      <Dialog open={showMandatoryDialog} onOpenChange={setShowMandatoryDialog}>
        <DialogHeader className={'hidden'}>
          <DialogTitle>Hi</DialogTitle>
        </DialogHeader>
        <DialogContent className="w-[calc(100%-32px)] max-w-md space-y-4 rounded-md p-6">
          <div className="flex justify-center">
            <div className="flex w-10 items-center justify-center rounded-full">
              <TriangleAlert className="h-10 w-10 text-muted-foreground" />
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            You cannot save details until all mandatory fields are filled.
          </p>

          <DialogClose asChild>
            <Button className="w-full">Close</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>

      {/* Form */}
      <form onSubmit={handleSubmit(onSaveDetails)}>
        <Card className="rounded-2xl border border-border shadow-sm">
          <CardHeader className="px-4 pb-3">
            <CardTitle className="text-base font-medium">Pre-process data</CardTitle>
          </CardHeader>

          <CardContent className="p-4 pt-0">
            <div className="space-y-3">
              {/* Coffee received kg */}
              <div className="space-y-1.5">
                <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                  Input coffee received (kg)<span className="">*</span>
                </Label>
                <div className="relative">
                  <Input
                    disabled={isSaving || isMoving}
                    type="number"
                    inputMode="decimal"
                    placeholder="0"
                    step="0.01"
                    className={cn(
                      'rounded-[10px] bg-white text-sm font-normal',
                      errors?.input_coffee_received_kg && 'border-red-500'
                    )}
                    {...register('input_coffee_received_kg', {
                      valueAsNumber: true,
                    })}
                  />
                  {errors?.input_coffee_received_kg && (
                    <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                  )}
                </div>
                {errors?.input_coffee_received_kg && (
                  <div className="text-xs text-red-500">{errors.input_coffee_received_kg.message}</div>
                )}
              </div>

              {/* Winnower waste */}
              <div className="space-y-1.5">
                <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                  Winnower waste (kg)<span className="">*</span>
                </Label>
                <div className="relative">
                  <Input
                    disabled={isSaving || isMoving}
                    type="number"
                    inputMode="decimal"
                    placeholder="0"
                    step="0.01"
                    className={cn(
                      'rounded-[10px] bg-white text-sm font-normal',
                      errors?.winnower_waste_kg && 'border-red-500'
                    )}
                    {...register('winnower_waste_kg', {
                      valueAsNumber: true,
                    })}
                  />
                  {errors?.winnower_waste_kg && (
                    <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                  )}
                </div>
                {errors?.winnower_waste_kg && (
                  <div className="text-xs text-red-500">{errors.winnower_waste_kg.message}</div>
                )}
              </div>

              {/* Siphon rejects */}
              <div className="space-y-1.5">
                <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                  Siphon rejects (kg)<span className="align-top">*</span>
                </Label>
                <div className="relative">
                  <Input
                    disabled={isSaving || isMoving}
                    type="number"
                    inputMode="decimal"
                    placeholder="0"
                    step="0.01"
                    className={cn(
                      'rounded-[10px] bg-white text-sm font-normal',
                      errors?.siphon_rejects_kg && 'border-red-500'
                    )}
                    {...register('siphon_rejects_kg', {
                      valueAsNumber: true,
                    })}
                  />
                  {errors?.siphon_rejects_kg && (
                    <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                  )}
                </div>
                {errors?.siphon_rejects_kg && (
                  <div className="text-xs text-red-500">{errors.siphon_rejects_kg.message}</div>
                )}
              </div>

              {/* Unripe coffees */}
              <div className="space-y-1.5">
                <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                  Unripe coffees (kg)<span className="align-top">*</span>
                </Label>
                <div className="relative">
                  <Input
                    disabled={isSaving || isMoving}
                    type="number"
                    inputMode="decimal"
                    placeholder="0"
                    step="0.01"
                    className={cn(
                      'rounded-[10px] bg-white text-sm font-normal',
                      errors?.unripe_coffees_kg && 'border-red-500'
                    )}
                    {...register('unripe_coffees_kg', {
                      valueAsNumber: true,
                    })}
                  />
                  {errors?.unripe_coffees_kg && (
                    <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                  )}
                </div>
                {errors?.unripe_coffees_kg && (
                  <div className="text-xs text-red-500">{errors.unripe_coffees_kg.message}</div>
                )}
              </div>

              {/* Ripe coffee for processing */}
              <div className="space-y-1.5">
                <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                  Ripe coffee for processing (kg)<span className="align-top">*</span>
                </Label>
                <div className="relative">
                  <Input
                    disabled={isSaving || isMoving}
                    type="number"
                    inputMode="decimal"
                    placeholder="0"
                    step="0.01"
                    className={cn(
                      'rounded-[10px] bg-white text-sm font-normal',
                      errors?.ripe_coffee_for_processing_kg && 'border-red-500'
                    )}
                    {...register('ripe_coffee_for_processing_kg', {
                      valueAsNumber: true,
                    })}
                  />
                  {errors?.ripe_coffee_for_processing_kg && (
                    <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                  )}
                </div>
                {errors?.ripe_coffee_for_processing_kg && (
                  <div className="text-xs text-red-500">{errors.ripe_coffee_for_processing_kg.message}</div>
                )}
              </div>

              {/* Completion of pre-processing */}
              <div className="space-y-1.5">
                <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                  Completion of pre-processing (kg)<span className="align-top">*</span>
                </Label>
                <Controller
                  name="completion_of_pre_processing_at"
                  control={control}
                  render={({ field }) => <DatePicker value={field.value} onChange={field.onChange} />}
                />
                {errors?.completion_of_pre_processing_at && (
                  <div className="text-xs text-red-500">{errors.completion_of_pre_processing_at.message}</div>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <Button type="submit" disabled={isSaving || isMoving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save details
              </Button>

              <Button type="button" onClick={handleSubmit(onMoveToProcessing)} disabled={isSaving || isMoving}>
                Move lot to processing
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default PreProcessEditPage;
